import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
  TableCell as MuiTableCell,
  styled,
  Tooltip,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import useAccessCode from "../hooks/useAccessCode";
import { IHistoryReservaciones } from "../models/codes";
import { CustomModal } from "../components/Modal";
import { Done, DoneAll, Home, Cancel } from "@mui/icons-material";
import { TableHeaderCell } from "../components/Table/TableHeaderCell";
import { TableCell } from "../components/Table/TableCell";
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';


export const HistorialReservaAdmin = () => {
  const today = new Date();
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  today.setHours(0, 0, 0, 0);

  const MobileTableCell = styled(MuiTableCell)(({ theme }) => ({
    padding: '4px 6px',
    fontSize: '0.7rem',
    lineHeight: '1.1',
    '&:first-of-type': {
      paddingLeft: '6px'
    },
    '&:last-of-type': {
      paddingRight: '6px'
    }
  }))

  const { getHistoryReservasAdmin,getMotivosAnulacion, deleteReserva } = useAccessCode();

  const [historyR, setHistoryR] = useState<IHistoryReservaciones[]>([]);
  const [itemSelect, setItemSelect] = useState<IHistoryReservaciones>({} as IHistoryReservaciones);
  const [motivosAnulacion, setMotivosAnulacion] = useState<any[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [vista, setVista] = useState<'tabla' | 'calendario'>('tabla');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showPreview, setShowPreview] = useState(false);
  const [motivoAnulacionId, setMotivoAnulacionId] = useState<number | ''>('');
  const [filtroEstado, setFiltroEstado] = useState<'todas' | 'Vigente' | 'Anulada'>('todas');
  const [dayReservationsModalOpen, setDayReservationsModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [
    selectedDateReservations,
    setSelectedDateReservations,
  ] = useState<IHistoryReservaciones[]>([]);


  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredHistoryR = React.useMemo(() => {
    if (filtroEstado === 'todas') {
      return historyR;
    }
    return historyR.filter(r => r.estado === filtroEstado);
  }, [historyR, filtroEstado]);

  // Calcular datos para la página actual
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentData = filteredHistoryR.slice(startIndex, endIndex);

  const handleOpen = () => setOpenModal(true);
  const handleClose = () => {
    setOpenModal(false);
  };

  const getStatusIcon = (item: IHistoryReservaciones) => {
    // Reserva completada (pasada)
    if (
      item.estado === 'Vigente' &&
      new Date(item.fecha_Reservacion) < new Date()
    ) {
      return {
        icon: <DoneAll fontSize="small" sx={{ color: 'blue' }} />,
        tooltip: 'Completada - No se puede anular',
      }
    }

    // Reserva pendiente (futura)
    if (
      item.estado === 'Vigente' &&
      new Date(item.fecha_Reservacion) >= new Date()
    ) {
      return {
        icon: <Done fontSize="small" sx={{ color: 'green' }} />,
        tooltip: 'Pendiente - Se puede anular',
      }
    }

    // Reserva anulada
    if (item.estado === 'Anulada') {
      return {
        icon: <Cancel fontSize="small" sx={{ color: '#FF6B6B' }} />,
        tooltip: 'Anulada'
      }
    }

    // Estado por defecto
    return {
      icon: <Done fontSize="small" color="disabled" />,
      tooltip: 'Estado desconocido'
    }
  }

  const canBeCancelled = (item: IHistoryReservaciones) => {
    // Solo se puede anular si está vigente Y la fecha es futura (mayor a hoy)
    return item.estado === "Vigente" && new Date(item.fecha_Reservacion) > new Date()
  }

  const handleCalendarEventClick = (reserva: IHistoryReservaciones) => {
    setItemSelect(reserva);
    setShowPreview(true);
  };

  const handleDateClick = (arg: any) => {
    const clickedDate = arg.date;
    const reservationsForDay = filteredHistoryR.filter(r => {
        const reservaDate = new Date(r.fecha_Reservacion);
        return reservaDate.getFullYear() === clickedDate.getFullYear() &&
               reservaDate.getMonth() === clickedDate.getMonth() &&
               reservaDate.getDate() === clickedDate.getDate();
    });

    if (reservationsForDay.length > 0) {
        setSelectedDate(clickedDate.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }));
        setSelectedDateReservations(reservationsForDay);
        setDayReservationsModalOpen(true);
    }
  };

  const handleDirectAnular = async () => {
    setShowPreview(false);
    
    if (!itemSelect.id_Reserva) return;

    const reservaParaAnular = {
      ...itemSelect,
      id_motivo_reserva: motivoAnulacionId
    }

    const response = await deleteReserva(reservaParaAnular);
    if (response) {
      setModalMessage("Reserva anulada con éxito");
      handleOpen();
      setHistoryR(
        historyR.map(item =>
          item.id_Reserva === itemSelect.id_Reserva
            ? { ...item, estado: "Anulada" }
            : item
        )
      );
    } else {
      setModalMessage("No es posible anular la reserva");
      handleOpen();
    }
    setMotivoAnulacionId('');
  }


  useEffect(() => {
    (async () => {
      let historyReservas = await getHistoryReservasAdmin();
      if (historyReservas) {
        setHistoryR(historyReservas);
      } else {
        setModalMessage("Error al obtener el historial de reservas");
        handleOpen();
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      let motivos = await getMotivosAnulacion();
      if (motivos) {
        setMotivosAnulacion(motivos);
      }
    })();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [filtroEstado]);

  return (
    <>
      <Box
        textAlign="center"
        display="flex"
        alignItems="center"
        flexDirection="column"
      >
        <Typography variant="h4" component="div" gutterBottom marginY="1em">
          Historial de Reservas
        </Typography>
        {/* Selector de vista */}
        <Box display="flex" gap={2} mb={2}>
          <Button
            variant={vista === 'tabla' ? 'contained' : 'outlined'}
            onClick={() => setVista('tabla')}
          >
            Vista Tabla
          </Button>
          <Button
            variant={vista === 'calendario' ? 'contained' : 'outlined'}
            onClick={() => setVista('calendario')}
          >
            Vista Calendario
          </Button>
        </Box>
        {/* Controles de filtro */}
        <Box display="flex" gap={2} mb={2}>
            <Button
                variant={filtroEstado === 'todas' ? 'contained' : 'outlined'}
                onClick={() => setFiltroEstado('todas')}
            >
                Todas
            </Button>
            <Button
                variant={filtroEstado === 'Vigente' ? 'contained' : 'outlined'}
                onClick={() => setFiltroEstado('Vigente')}
            >
                Vigentes
            </Button>
            <Button
                variant={filtroEstado === 'Anulada' ? 'contained' : 'outlined'}
                onClick={() => setFiltroEstado('Anulada')}
            >
                Anuladas
            </Button>
        </Box>
        {/* Vista de tabla */}
        {vista === 'tabla' && (
          <TablaHistorialReservas
            data={currentData}
            totalCount={filteredHistoryR.length}
            isSmallScreen={isSmallScreen}
            MobileTableCell={MobileTableCell}
            getStatusIcon={getStatusIcon}
            canBeCancelled={canBeCancelled}
            setItemSelect={setItemSelect}
            setShowPreview={setShowPreview}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
          />
        )}
        {/* Vista de calendario */}
        {vista === 'calendario' && (
          <Box width={1} maxWidth={1000}>
            <CalendarHistorialReservas
              reservas={filteredHistoryR}
              canBeCancelled={canBeCancelled}
              onEventClick={handleCalendarEventClick}
              onDateClick={handleDateClick}
            />
          </Box>
        )}
        <CustomModal openState={openModal} onCloseHandler={handleClose}>
          <Typography variant="h4" component="div" gutterBottom marginY="1em">
            {modalMessage}
          </Typography>

          <Button
            color="secondary"
            variant="outlined"
            endIcon={<Home />}
            sx={{ marginY: 2, width: "50%" }}
            onClick={handleClose}
          >
            Inicio
          </Button>
        </CustomModal>
        <CustomModal
          openState={showPreview}
          onCloseHandler={() => {
            setShowPreview(false);
            setMotivoAnulacionId("");
          }}
        >
          <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
            Detalles de la Reserva
          </Typography>
          {itemSelect.id_Reserva && (
            <Box sx={{ width: '60%' }}>
              <Typography sx={{ mb: 1 }}><strong>Amenidad:</strong> {itemSelect.amenidad}</Typography>
              <Typography sx={{ mb: 1 }}><strong>Inmueble:</strong> {itemSelect.num_casa}</Typography>
              <Typography sx={{ mb: 1 }}>
                <strong>Fecha:</strong>{' '}
                {new Date(itemSelect.fecha_Reservacion).toLocaleDateString('es-ES', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })}
              </Typography>
              <Typography sx={{ mb: 1 }}><strong>Hora:</strong> {itemSelect.hora}</Typography>
              <Typography>
                <strong>Valor:</strong> Q
                {itemSelect.valor?.toString().split('.').length === 2
                  ? itemSelect.valor
                  : `${itemSelect.valor}.00`}
              </Typography>
              {canBeCancelled(itemSelect) && (
                <>
                  <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel id="motivo-anulacion-label">
                      Motivo de Anulación
                    </InputLabel>
                    <Select
                      labelId="motivo-anulacion-label"
                      id="motivo-anulacion-select"
                      value={motivoAnulacionId}
                      label="Motivo de Anulación"
                      onChange={(e) =>
                        setMotivoAnulacionId(e.target.value as number)
                      }
                    >
                      {motivosAnulacion.map((motivo) => (
                        <MenuItem key={motivo.id} value={motivo.id}>
                          {motivo.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {motivoAnulacionId && (
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 2,
                        fontStyle: "italic",
                        color: "text.secondary",
                      }}
                    >
                      <strong>Descripción:</strong>{" "}
                      {
                        motivosAnulacion.find(
                          (m) => m.id === motivoAnulacionId
                        )?.descripcion
                      }
                    </Typography>
                  )}
                </>
              )}
            </Box>
          )}
          <Box
            sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                setShowPreview(false);
                setMotivoAnulacionId("");
              }}
            >
              Cerrar
            </Button>
            {canBeCancelled(itemSelect) && (
              <Button
                variant="contained"
                color="error"
                onClick={handleDirectAnular}
                disabled={!motivoAnulacionId}
              >
                Anular Reserva
              </Button>
            )}
          </Box>
        </CustomModal>
        <CustomModal
          openState={dayReservationsModalOpen}
          onCloseHandler={() => setDayReservationsModalOpen(false)}
        >
          <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
            Reservas para {selectedDate}
          </Typography>
          <Box sx={{ width: '100%', textAlign: 'left' }}>
            {selectedDateReservations.map(reserva => (
              <Box
                key={reserva.id_Reserva}
                sx={{
                  p: 1,
                  my: 1,
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
                onClick={() => {
                  handleCalendarEventClick(reserva);
                  setDayReservationsModalOpen(false);
                }}
              >
                <Typography><strong>Amenidad:</strong> {reserva.amenidad}</Typography>
                <Typography><strong>Inmueble:</strong> {reserva.num_casa}</Typography>
                <Typography><strong>Hora:</strong> {reserva.hora}</Typography>
                <Typography><strong>Estado:</strong> {reserva.estado}</Typography>
              </Box>
            ))}
          </Box>
          <Button
            variant="outlined"
            onClick={() => setDayReservationsModalOpen(false)}
            sx={{ mt: 2 }}
          >
            Cerrar
          </Button>
        </CustomModal>
      </Box>
    </>
  );
};

// Componente para la tabla de historial
const TablaHistorialReservas = ({
  data,
  totalCount,
  isSmallScreen,
  MobileTableCell,
  getStatusIcon,
  canBeCancelled,
  setItemSelect,
  setShowPreview,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
}) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      width={0.96}
      maxWidth={1000}
    >
      <TableContainer component={Box} sx={{ width: "100%" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell sx={{ whiteSpace: "nowrap", padding: 1 }}>
                Amenidad
              </TableHeaderCell>
              <TableHeaderCell sx={{ whiteSpace: "nowrap", padding: 1 }}>
                Casa
              </TableHeaderCell>
              <TableHeaderCell sx={{ whiteSpace: "nowrap", padding: 1 }}>
                #
              </TableHeaderCell>
              <TableHeaderCell sx={{ whiteSpace: "nowrap", padding: 1 }}>
                Fecha Hora
              </TableHeaderCell>
              <TableHeaderCell sx={{ whiteSpace: "nowrap", padding: 1 }}>
                Valor
              </TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length > 0 ? (
              data.map((item, index) => {
                const TableCellComponent = isSmallScreen ? MobileTableCell : MuiTableCell;
                const status = getStatusIcon(item);
                
                return (
                  <TableRow
                    key={item.id_Reserva}
                    sx={{
                      cursor: "pointer",
                      backgroundColor: canBeCancelled(item)
                        ? "rgba(1, 52, 103, 0.02)"
                        : "transparent",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: "rgba(1, 52, 103, 0.08)",
                        transform: canBeCancelled(item)
                          ? "translateX(2px)"
                          : "none",
                      },
                    }}
                    onClick={() => {
                      setItemSelect(item);
                      setShowPreview(true);
                    }}
                  >
                    <TableCellComponent>
                      <Box
                        component="span"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          maxWidth: '100%',
                          overflow: 'hidden'
                        }}
                      >
                        <Tooltip title={status.tooltip}>
                          {status.icon}
                        </Tooltip>
                        {item.amenidad}
                      </Box>
                    </TableCellComponent>
                    <TableCell>
                      {item.num_casa}
                    </TableCell>
                    <TableCell
                      sx={{
                        whiteSpace: "nowrap",
                        padding: 1,
                      }}
                    >
                      {item.num_Reserva}
                    </TableCell>
                    <TableCell
                      sx={{
                        whiteSpace: "nowrap",
                        padding: 1,
                      }}
                    >
                      {new Date(item.fecha_Reservacion).toLocaleDateString(
                        "es-ES",
                        {
                          day: "2-digit",
                          month: "2-digit",
                          year: "2-digit",
                        }
                      )}
                      {" "}
                      {item.hora.replace(" ", "")}
                    </TableCell>
                    <TableCell
                      sx={{
                        whiteSpace: "nowrap",
                        padding: 1,
                      }}
                    >
                      Q
                      {item.valor.toString().split(".").length === 2
                        ? item.valor
                        : item.valor.toString() + ".00"}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No hay datos para mostrar en esta página
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Filas por página:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
        }
      />
    </Box>
  );
};

// Componente para adaptar reservas a eventos de calendario
const CalendarHistorialReservas = ({
  reservas,
  canBeCancelled,
  onEventClick,
  onDateClick,
}) => {
  const eventos = reservas.map((item) => {
    let fecha = item.fecha_Reservacion;
    if (fecha.includes("/")) {
      const [mes, dia, anioYhora] = fecha.split('/');
      const [anio, horaRest] = anioYhora.trim().split(' ');
      fecha = `${anio}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
    }
    let horaInicio = '00:00', horaFin = '01:00';
    if (item.hora && item.hora.includes('-')) {
      const partes = item.hora.trim().split('-');
      horaInicio = partes[0].padStart(5, '0');
      horaFin = partes[1] ? partes[1].padStart(5, '0') : '01:00';
    }
    const start = `${fecha}T${horaInicio}:00`;
    const end = `${fecha}T${horaFin}:00`;
    return {
      title: `${item.amenidad} (${horaInicio}-${horaFin})`,
      start,
      end,
      color: item.estado === 'Anulada' ? '#FF6B6B' : undefined,
      extendedProps: {
        fullTitle: `${item.amenidad} (${horaInicio}-${horaFin}) - ${item.estado}`,
        reserva: item
      }
    };
  });

  const handleEventClick = (clickInfo) => {
    const reserva = clickInfo.event.extendedProps.reserva;
    onEventClick(reserva);
  };

  return (
    <Box>
      <style>
        {`
          .fc-event {
            color: white !important;
            font-weight: normal !important;
          }
          .fc-event-title {
            color: white !important;
            font-weight: normal !important;
          }
        `}
      </style>
      <FullCalendar
        plugins={[dayGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        locale={esLocale}
        height={600}
        events={eventos}
        headerToolbar={{
          left: 'title',
          center: '',
          right: 'prev,next today'
        }}
        editable={false}
        selectable={true}
        eventClick={handleEventClick}
        dateClick={onDateClick}
        eventDidMount={(info) => {
          info.el.setAttribute("title", info.event.extendedProps.fullTitle);
          info.el.style.cursor = "pointer";
        }}
      />
    </Box>
  );
};
